import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import driverService from '../services/driverService';
import DriverForm from '../components/drivers/DriverForm';
import Modal from '../components/common/Modal';
import Table from '../components/common/Table';
import PageHeader from '../components/common/PageHeader';
import Badge from '../components/common/Badge';
import { getInitials } from '../utils/formatters';
import toast from 'react-hot-toast';

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState({ open: false, driver: null });
  const [syncMeta, setSyncMeta] = useState(null);
  const [reconcile, setReconcile] = useState({
    previewOpen: false,
    preview: null,
    loadingPreview: false,
    applying: false,
  });

  const asCount = (value) => {
    if (Array.isArray(value)) return value.length;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await driverService.getAllWithMeta();
      setDrivers(Array.isArray(data?.rows) ? data.rows : []);
      setSyncMeta(data?.sync || null);
    } catch {
      setDrivers([]);
      setSyncMeta(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runPreview = async () => {
    if (syncMeta?.secret_configured === false) {
      toast.error('CLERK_SECRET_KEY is missing. Reconciliation cannot be trusted.');
      return;
    }

    setReconcile((prev) => ({ ...prev, loadingPreview: true }));
    try {
      const preview = await driverService.reconcileClerk(true);
      setReconcile((prev) => ({
        ...prev,
        preview,
        previewOpen: true,
      }));
    } catch {
      toast.error('Failed to fetch reconciliation preview.');
    } finally {
      setReconcile((prev) => ({ ...prev, loadingPreview: false }));
    }
  };

  const applyReconciliation = async () => {
    setReconcile((prev) => ({ ...prev, applying: true }));
    try {
      const result = await driverService.reconcileClerk(false);
      toast.success(result?.summary || 'Reconciliation completed.');
      setReconcile((prev) => ({
        ...prev,
        previewOpen: false,
        preview: null,
      }));
      load();
    } catch {
      toast.error('Failed to run reconciliation cleanup.');
    } finally {
      setReconcile((prev) => ({ ...prev, applying: false }));
    }
  };

  const staleCount = drivers.filter((d) => !d?.synced_with_clerk || !d?.clerk_user_id).length;

  const columns = [
    { key: 'full_name', label: 'Driver', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 600, flexShrink: 0 }}>{getInitials(v)}</div>
        <div>
          <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>
            <Link to={`/drivers/${row.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>{v}</Link>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{row.phone}</div>
        </div>
      </div>
    ) },
    { key: 'phone', label: 'Phone', render: (v) => v || '—' },
    { key: 'national_id', label: 'National ID', render: (v) => v || '—' },
    { key: 'nhif_number', label: 'NHIF', render: (v) => v || '—' },
    {
      key: 'clerk_user_id',
      label: 'Clerk Link',
      render: (v, row) => {
        if (!v) return <Badge status="warning" label="Unlinked" />;
        if (!row?.synced_with_clerk) return <Badge status="warning" label="Stale" />;
        return <Badge status="success" label="Linked" />;
      },
    },
    { key: 'status', label: 'Status', render: (v) => <Badge status={v} label={v} /> },
    { key: 'id', label: '', render: (v, row) => (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Link to={`/drivers/${row.id}`} className="btn btn-secondary btn-sm">View</Link>
        <button className="btn btn-secondary btn-sm" onClick={() => setModal({ open: true, driver: row })}>Edit</button>
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Drivers" subtitle={`${drivers.length} registered drivers`}
        actions={(
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={runPreview} disabled={reconcile.loadingPreview || syncMeta?.secret_configured === false}>
              {reconcile.loadingPreview ? 'Checking...' : 'Reconcile Clerk'}
            </button>
            <button className="btn btn-primary" onClick={() => setModal({ open: true, driver: null })}>+ Add Driver</button>
          </div>
        )}
      />

      {syncMeta && (
        <div
          style={{
            border: `1px solid ${syncMeta.secret_configured === false ? 'var(--warning-border, #f59e0b)' : 'var(--gray-200)'}`,
            borderRadius: 'var(--radius-md)',
            padding: '0.75rem 0.9rem',
            marginBottom: '1rem',
            fontSize: '0.86rem',
            color: syncMeta.secret_configured === false ? 'var(--warning-text)' : 'var(--gray-600)',
            background: syncMeta.secret_configured === false ? 'var(--warning-light)' : 'var(--gray-50)',
            display: 'grid',
            gap: '0.35rem',
          }}
        >
          <div>
            Sync status: enabled={String(Boolean(syncMeta?.enabled))}, secret_configured={String(Boolean(syncMeta?.secret_configured))}, created={asCount(syncMeta?.created)}, updated={asCount(syncMeta?.updated)}.
          </div>
          {syncMeta?.error ? <div>Sync error: {String(syncMeta.error)}</div> : null}
          {syncMeta.secret_configured === false ? (
            <div>CLERK_SECRET_KEY is missing on backend. Reconciliation cannot be trusted.</div>
          ) : null}
          {staleCount > 0 ? <div>Potential stale/unlinked local driver records: {staleCount}.</div> : null}
        </div>
      )}

      {!loading && drivers.length === 0 && (
        <div
          style={{
            border: '1px solid var(--gray-200)',
            borderRadius: 'var(--radius-md)',
            padding: '0.75rem 0.9rem',
            marginBottom: '1rem',
            fontSize: '0.86rem',
            color: 'var(--gray-600)',
            background: 'var(--gray-50)',
          }}
        >
          Driver records are sourced from the backend at /api/drivers/. If Clerk users are missing,
          verify backend CLERK_SECRET_KEY is configured so sync can run before list retrieval.
        </div>
      )}

      <div className="card">
        <Table columns={columns} data={drivers} loading={loading} emptyMessage="No drivers registered yet" />
      </div>
      <Modal isOpen={modal.open} onClose={() => setModal({ open: false, driver: null })}
        title={modal.driver ? 'Edit Driver' : 'Add Driver'}>
        <DriverForm driver={modal.driver} onCancel={() => setModal({ open: false, driver: null })}
          onSuccess={() => { setModal({ open: false, driver: null }); load(); }} />
      </Modal>

      <Modal
        isOpen={reconcile.previewOpen}
        onClose={() => setReconcile((prev) => ({ ...prev, previewOpen: false }))}
        title="Reconcile Drivers with Clerk"
        footer={(
          <>
            <button className="btn btn-secondary" onClick={() => setReconcile((prev) => ({ ...prev, previewOpen: false }))}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={applyReconciliation} disabled={reconcile.applying || syncMeta?.secret_configured === false}>
              {reconcile.applying ? 'Applying...' : 'Confirm Cleanup'}
            </button>
          </>
        )}
      >
        <p style={{ marginTop: 0, color: 'var(--gray-600)' }}>
          Preview was generated with dry_run=true. Confirm cleanup to run dry_run=false.
        </p>

        <div style={{ display: 'grid', gap: '0.45rem', fontSize: '0.9rem' }}>
          <div>Kept: <strong>{asCount(reconcile.preview?.kept)}</strong></div>
          <div>Stale candidates: <strong>{asCount(reconcile.preview?.stale_candidates)}</strong></div>
          <div>Will delete: <strong>{asCount(reconcile.preview?.deleted)}</strong></div>
          <div>Skipped unlinked: <strong>{asCount(reconcile.preview?.skipped_unlinked)}</strong></div>
          <div>Skipped with activity: <strong>{asCount(reconcile.preview?.skipped_with_activity)}</strong></div>
          <div>Skipped self: <strong>{asCount(reconcile.preview?.skipped_self)}</strong></div>
        </div>

        {reconcile.preview?.summary ? (
          <p style={{ marginBottom: 0, marginTop: '0.9rem', color: 'var(--gray-600)' }}>
            Summary: {reconcile.preview.summary}
          </p>
        ) : null}
      </Modal>
    </div>
  );
}